import { Box, Divider, Theme, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import { IputSearchRef } from '@uiKit/SearchInput';
import { useBarcodeScanner } from 'src/hooks/useBarcodeReader';
import { ReviewItemsFilterType } from 'src/types/shared';

import { CaptureId, CurrentSessionProps, DrawerTypes, IdV4, MapCapturePiece } from '../../types';
import ItemsList from './components/ItemsList';
import PieceView from './components/PieceView';
import ReviewStats from './components/Stats';
import { prepareItems } from './helpers';
import { containerSx, internalContainerSx, leftSx, sxDivider, sxRightContainer } from './styles';

type Props = {
  mapCapturePiece: MapCapturePiece;
  setDrawer: (type: DrawerTypes) => void;
  onItemClick: (key: CaptureId | null) => void;
  onCropClick: () => void;
  retryFailedPieces: (id: IdV4) => void;
  currentSession: CurrentSessionProps;
  onDeletePiece: () => void;
  cursor: CaptureId | null;
  isDrawerActive: boolean;
};

const Review = ({
  mapCapturePiece,
  cursor,
  onItemClick,
  currentSession,
  setDrawer,
  onCropClick,
  retryFailedPieces,
  onDeletePiece,
  isDrawerActive,
}: Props) => {
  const activePiece = mapCapturePiece[cursor || 0];
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const [filter, setFilter] = useState<null | ReviewItemsFilterType>(null);
  const pieces = Object.values(mapCapturePiece);
  const [search, setSearch] = useState('');
  const selectOnChange = useRef(false);
  const searchInputRef = useRef<IputSearchRef>(null);

  const activePieceId = activePiece?.id;

  const { items, isActiveInList, ...stats } = prepareItems(pieces, filter, activePieceId, search);

  if (!isActiveInList && activePieceId) onItemClick(null);

  // select first item if input was barcode
  useEffect(() => {
    if (selectOnChange.current) {
      if (items[0]) {
        if (cursor !== items[0].id) {
          onItemClick(items[0].id);
        }
      } else if (cursor) {
        onItemClick(null);
      }
      selectOnChange.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useBarcodeScanner(
    (value) => {
      setSearch(value);
      searchInputRef.current?.setValue(value);
      searchInputRef.current?.element?.focus();
      selectOnChange.current = true;
    },
    cursor,
    { enabled: !isDrawerActive },
  );

  return (
    <Box sx={containerSx}>
      <Box sx={internalContainerSx}>
        <Box sx={leftSx(isMobile, !!activePiece)}>
          <ReviewStats {...stats} total={pieces.length} onFilterClick={setFilter} />
          <ItemsList
            selected={activePieceId}
            onClear={() => setFilter(null)}
            onItemClick={onItemClick}
            items={items}
            filter={filter}
            setSearch={setSearch}
            searchRef={searchInputRef}
          />
        </Box>
        <Divider sx={sxDivider} orientation="vertical" />
        <Box sx={sxRightContainer(isMobile, !!activePiece)}>
          <PieceView
            onDeletePiece={onDeletePiece}
            onCropClick={onCropClick}
            retryFailedPieces={retryFailedPieces}
            handleBack={() => onItemClick(null)}
            setDrawer={setDrawer}
            capturePiece={activePiece}
            currentSession={currentSession}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Review;
